import React, { useState, useEffect, useRef } from 'react';
import { Clock, Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { SEO } from '../../components/SEO';

function Timer50Seconds() {
  const [timeLeft, setTimeLeft] = useState<number>(50);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(100);
  const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(true);
  const [audioError, setAudioError] = useState<boolean>(false);
  
  const intervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const totalTime = 50; // 50 seconds

  useEffect(() => {
    // Initialize audio
    audioRef.current = new Audio();
    audioRef.current.src = 'https://assets.mixkit.co/active_storage/sfx/989/989-preview.mp3';
    audioRef.current.loop = true;

    // Add error handling for audio loading
    audioRef.current.addEventListener('error', () => {
      console.error('Error loading audio file');
      setAudioError(true);
      setIsSoundEnabled(false);
    });
    
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const startTimer = () => {
    if (isFinished) {
      resetTimer();
    }
    
    setIsRunning(true);
    
    const now = Date.now();
    if (pausedTimeRef.current > 0) {
      // Resuming from pause
      startTimeRef.current = now - ((totalTime - timeLeft) * 1000);
    } else {
      // Starting fresh
      startTimeRef.current = now;
    }
    
    intervalRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const remaining = Math.max(0, totalTime - elapsed);
      
      if (remaining <= 0) {
        clearInterval(intervalRef.current!);
        setTimeLeft(0);
        setIsRunning(false);
        setIsFinished(true);
        setProgress(0);
        
        // Play sound when timer finishes
        if (isSoundEnabled && audioRef.current && !audioError) {
          audioRef.current.play().catch(e => {
            console.error("Error playing sound:", e);
            setAudioError(true);
            setIsSoundEnabled(false);
          });
        }
      } else {
        setTimeLeft(remaining);
        setProgress((remaining / totalTime) * 100);
      }
    }, 10); // Update every 10ms for smooth progress
  };

  const pauseTimer = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      pausedTimeRef.current = timeLeft;
      setIsRunning(false);
    }
  };

  const resetTimer = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }
    setTimeLeft(totalTime);
    setIsRunning(false);
    setIsFinished(false);
    setProgress(100);
    pausedTimeRef.current = 0;
    
    // Stop sound
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const formatTime = (seconds: number): string => {
    const ms = Math.floor((seconds % 1) * 100);
    const s = Math.floor(seconds);
    return `${s}.${ms.toString().padStart(2, '0')}`;
  };

  const handleStartStop = () => {
    if (isRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
  };
  
  const toggleSound = () => {
    if (audioError) {
      return; // Don't allow enabling sound if there was an error
    }
    setIsSoundEnabled(!isSoundEnabled);
    if (isFinished && audioRef.current) {
      if (isSoundEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => {
          console.error("Error playing sound:", e);
          setAudioError(true);
          setIsSoundEnabled(false);
        });
      }
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Timer 50 Sekundi",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online timer od 50 sekundi. Precizno odbrojavanje s vizualnim prikazom preostalog vremena.",
    "url": "https://online-kalkulator.com/online-stoperica/timer-50-sekundi",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Timer 50 Sekundi | Precizno Odbrojavanje"
        description="Besplatni online timer od 50 sekundi. Precizno odbrojavanje s vizualnim prikazom preostalog vremena."
        canonicalUrl="/online-stoperica/timer-50-sekundi"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Clock className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Timer 50 Sekundi</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Timer Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Time Display */}
              <div className="text-center">
                <div className={`text-6xl font-mono font-bold tracking-wider ${isFinished ? 'text-red-600' : 'text-blue-600'}`}>
                  {formatTime(timeLeft)}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Controls */}
              <div className="flex justify-center gap-4">
                <button
                  onClick={handleStartStop}
                  className={`flex items-center justify-center w-14 h-14 rounded-full ${
                    isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                  } text-white transition-colors`}
                  aria-label={isRunning ? 'Pause' : 'Start'}
                >
                  {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                
                <button
                  onClick={resetTimer}
                  className={`flex items-center justify-center w-14 h-14 rounded-full bg-gray-500 hover:bg-gray-600 text-white transition-colors ${
                    timeLeft === totalTime && !isRunning ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={timeLeft === totalTime && !isRunning}
                  aria-label="Reset"
                >
                  <RotateCcw className="w-6 h-6" />
                </button>
                
                <button
                  onClick={toggleSound}
                  className={`flex items-center justify-center w-14 h-14 rounded-full ${
                    audioError ? 'bg-gray-300 cursor-not-allowed' :
                    isSoundEnabled ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-400 hover:bg-gray-500'
                  } text-white transition-colors`}
                  aria-label={isSoundEnabled ? 'Mute' : 'Unmute'}
                  disabled={audioError}
                >
                  {isSoundEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
                </button>
              </div>

              {/* Status Messages */}
              {audioError && (
                <div className="text-center py-3 px-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600">
                    Zvuk nije dostupan. Provjerite postavke zvuka vašeg uređaja.
                  </p>
                </div>
              )}

              {/* Finished Message */}
              {isFinished && (
                <div className="text-center py-3 px-4 bg-red-50 rounded-lg">
                  <p className="text-lg font-medium text-red-600">
                    Vrijeme je isteklo!
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Timeru od 50 Sekundi</h2>
                <p className="text-gray-600 leading-relaxed">
                  Timer od 50 sekundi je koristan alat za precizno mjerenje kratkog vremenskog intervala.
                  Idealan je za situacije kada trebate kratko odbrojavanje, poput vježbi, kuhanja,
                  ili drugih aktivnosti koje zahtijevaju kratko vremensko ograničenje.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Kliknite na gumb <span className="text-green-600">▶</span> za pokretanje timera</li>
                  <li>Timer će automatski odbrojavati od 50 sekundi do 0</li>
                  <li>Kliknite na gumb <span className="text-red-600">❚❚</span> za pauziranje timera</li>
                  <li>Kliknite na gumb <span className="text-gray-600">↺</span> za resetiranje timera</li>
                  <li>Kliknite na gumb <span className="text-blue-600">🔊</span>/<span className="text-gray-600">🔇</span> za uključivanje/isključivanje zvuka</li>
                  <li>Kada vrijeme istekne, prikazat će se poruka "Vrijeme je isteklo!" i oglasit će se zvučni alarm</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Vježbe istezanja i zadržavanja položaja</li>
                  <li>Kuhanje i priprema hrane</li>
                  <li>Intervalni treninzi</li>
                  <li>Vježbe disanja i meditacije</li>
                  <li>Mjerenje vremena za četkanje zubi</li>
                  <li>Igre koje zahtijevaju vremensko ograničenje</li>
                  <li>Kratke prezentacije i govori</li>
                  <li>Pomodoro tehnika (kratke pauze)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Ostali Dostupni Timeri</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <a href="/online-stoperica/timer-1-sekunda" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 1 Sekunda</span>
                  </a>
                  <a href="/online-stoperica/timer-2-sekunde" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 2 Sekunde</span>
                  </a>
                  <a href="/online-stoperica/timer-5-sekundi" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 5 Sekundi</span>
                  </a>
                  <a href="/online-stoperica/timer-10-sekundi" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 10 Sekundi</span>
                  </a>
                  <a href="/online-stoperica/timer-20-sekundi" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 20 Sekundi</span>
                  </a>
                  <a href="/online-stoperica/timer-30-sekundi" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 30 Sekundi</span>
                  </a>
                  <a href="/online-stoperica/timer-40-sekundi" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 40 Sekundi</span>
                  </a>
                  <a href="/online-stoperica/timer-60-sekundi" className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <span className="text-gray-700 hover:text-blue-700">Timer 60 Sekundi</span>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Timer50Seconds;